import { useSeoMeta } from '@unhead/react';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { ZapPollCard, ZapPollCardSkeleton } from '@/components/ZapPollCard';
import { usePolls } from '@/hooks/usePolls';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, Vote as VoteIcon, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ROUND1_EVENT_ID } from '@/lib/constants';

export default function Round1Page() {
  const { data: polls, isLoading, isError } = usePolls(ROUND1_EVENT_ID);

  useSeoMeta({
    title: 'Round 1 Results - Bitcoin Maxi Madness',
    description: 'View the completed Round 1 matchups from the Bitcoin Maxi Madness bracket challenge.',
  });

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <main>
        {/* Page hero */}
        <section className="relative py-10 sm:py-14 overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(247,147,26,0.04)_0%,transparent_60%)]" />

          <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors mb-6"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>

            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Badge variant="secondary" className="text-xs px-3 py-1 tracking-wider uppercase font-medium border border-border/50">
                  <CheckCircle2 className="w-3 h-3 mr-1.5" />
                  Round 1 — Completed
                </Badge>
              </div>

              <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground">
                Round 1 <span className="text-muted-foreground">Results</span>
              </h1>

              <p className="text-muted-foreground text-sm sm:text-base max-w-xl leading-relaxed">
                Round 1 is in the books! Here are the final results from every matchup.
                The winners have advanced to Round 2.
              </p>

              {/* Round 2 link */}
              <Link
                to="/vote"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
              >
                Vote in Round 2 →
              </Link>
            </div>
          </div>
        </section>

        {/* Polls list */}
        <section className="relative pb-16 sm:pb-24">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            {isLoading ? (
              <div className="space-y-4">
                {Array.from({ length: 8 }).map((_, i) => (
                  <ZapPollCardSkeleton key={i} />
                ))}
              </div>
            ) : isError ? (
              <Card className="border-destructive/20 bg-destructive/5">
                <CardContent className="py-12 px-8 text-center">
                  <AlertCircle className="w-10 h-10 text-destructive mx-auto mb-3" />
                  <p className="text-muted-foreground">
                    Failed to load polls. Please check your relay connections and try again.
                  </p>
                </CardContent>
              </Card>
            ) : polls && polls.length > 0 ? (
              <div className="space-y-4">
                {polls.map((poll, i) => (
                  <ZapPollCard key={poll.event.id} poll={poll} index={i} />
                ))}

                <p className="text-center text-xs text-muted-foreground/50 pt-4">
                  {polls.length} Round 1 matchup{polls.length !== 1 ? 's' : ''} found
                </p>
              </div>
            ) : (
              <Card className="border-dashed border-border/50 bg-card/50">
                <CardContent className="py-16 px-8 text-center">
                  <div className="max-w-sm mx-auto space-y-4">
                    <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                      <VoteIcon className="w-8 h-8 text-primary/60" />
                    </div>
                    <p className="text-muted-foreground text-sm">
                      No Round 1 polls found.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
